import { Location } from '@angular/common';
import { inject, Injectable, Provider } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { INavigationService, INavigationServiceToken } from './navigation-service.interface';

@Injectable({
  providedIn: 'root'
})
export class NavigationService implements INavigationService {
  private readonly router: Router;
  private readonly location: Location;

  private history: string[] = [];

  private routerEventSubscription: Subscription | undefined;

  public activeComponentSubject: BehaviorSubject<string | undefined>;

  constructor(
  ) {
    this.router = inject(Router);
    this.location = inject(Location);

    this.activeComponentSubject = new BehaviorSubject<string | undefined>(undefined);

    if (!this.routerEventSubscription) {
      this.routerEventSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.history.push(event.urlAfterRedirects);
          this.activeComponentSubject.next(event.urlAfterRedirects);
        }
      });
    }
  }

  public refresh(): void {
    let url = this.router.url.split('?')[0];
    let urlTree = this.router.parseUrl(this.router.url);
    let queryParams = urlTree.queryParams;

    this.router.navigateByUrl('/', { skipLocationChange: true })
      .then(
        () => {
          this.router.navigate([`${url}`], { queryParams: queryParams });
        }
      );
  }

  public back(): void {
    this.history.pop();

    if (this.history.length > 0) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  public toErrorPage(message: string): void {
    this.router.navigate([ENVIRONMENT.errorLandingPage], { state: { message: message } });
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const NavigationServiceProvider: Provider[] = [
  { provide: INavigationServiceToken, useExisting: NavigationService }
];
